import {isValidImageCoordinates} from './imageCoordinates';

export class MapLayers {
    constructor(map, parent) {
        this.map = map;
        this.parent = parent
    }

    init() {
        /* Add land */
        if (!this.layerExists("land")) {
            this.map.addLayer({
                'id': "land",
                'source': "geojson",
                'type': 'fill',
                'filter': ["==", "resource_type", "land"],
                'layout': {
                    'visibility': 'visible'
                },
                'paint': {
                    'fill-color': ['get', 'color'],
                    'fill-opacity': ['get', 'opacity']
                }
            });
        }

        if (!this.layerExists("land_lines")) {
            this.map.addLayer({
                'id': "land_lines",
                'type': 'line',
                'source': "geojson",
                'filter': ["==", "resource_type", "land"],
                'layout': {
                    'visibility': 'visible'
                },
                'paint': {
                    'line-color': ['get', 'borderColor'],
                    'line-width': ['get', 'borderWidth'],
                    'line-opacity': ['get', 'borderOpacity']
                }
            });
        }

        /* Add fields */
        if (!this.layerExists('fields_line')) {
            this.map.addLayer({
                'id': 'fields_line',
                'type': 'line',
                'source': 'geojson',
                'filter': ["==", "resource_type", "field"],
                'layout': {
                    'visibility': 'visible'
                },
                'paint': {
                    'line-opacity': ['get', 'borderOpacity'],
                    'line-color': ['get', 'borderColor'],
                    'line-width': ['get', 'borderWidth']
                }
            });
        }

        /* Add no-agro zones */
        if (!this.layerExists("gap_lines")) {
            this.map.addLayer({
                'id': "gap_lines",
                'type': 'line',
                'source': "geojson",
                'filter': ["==", "resource_type", "gap"],
                'layout': {
                    'visibility': 'visible'
                },
                'paint': {
                    'line-opacity': ['get', 'borderOpacity'],
                    'line-color': ['get', 'borderColor'],
                    'line-width': ['get', 'borderWidth']
                }
            });
        }
    }

    addImage(imageId, imageUrl, polygon, bbox, options = {}) {
        if (!isValidImageCoordinates(bbox)) {
            return false;
        }
        const opacity = options.opacity ?? 1;
        const resampling = options.resampling ?? 'linear';

        if (!this.sourceExists(imageId)) {
            this.map.addSource(imageId, {
                type: 'image',
                url: imageUrl,
                coordinates: bbox,
            });
        } else {
            this.map.getSource(imageId).updateImage({url: imageUrl, coordinates: bbox});
        }

        if (!this.layerExists(imageId)) { 
            this.map.addLayer({
                id: imageId,
                type: "raster",
                source: imageId,
                layout: {
                    visibility: 'visible'
                },
                paint: {
                    "raster-opacity": opacity,
                    "raster-resampling": resampling,
                },
            });
            return;
        }

        this.map.setLayoutProperty(imageId, 'visibility', 'visible');
        this.setRasterOpacity(imageId, opacity);
        this.map.setPaintProperty(imageId, 'raster-resampling', resampling);
    }

    addCanvas(layerId, canvas, bbox, options = {}) {
        const opacity = options.opacity ?? 1;

        if (!this.sourceExists(layerId)) {
            this.map.addSource(layerId, {
                type: 'canvas',
                canvas,
                coordinates: bbox,
                animate: true,
            });
        }

        if (!this.layerExists(layerId)) {
            this.map.addLayer({
                id: layerId,
                type: "raster",
                source: layerId,
                layout: {
                    visibility: 'visible'
                },
                paint: {
                    "raster-opacity": opacity,
                },
            });
            return;
        }

        this.map.setLayoutProperty(layerId, 'visibility', 'visible');
        this.setRasterOpacity(layerId, opacity);

        const source = this.map.getSource(layerId);
        source?.play?.();
    }

    setLayerVisibility(layerId, visibility) {
        if (!this.layerExists(layerId)) {
            return;
        }

        this.map.setLayoutProperty(layerId, 'visibility', visibility);
    }

    setRasterOpacity(layerId, opacity) {
        if (!this.layerExists(layerId)) {
            return;
        }

        this.map.setPaintProperty(layerId, 'raster-opacity', opacity);
    }

    setFillOpacity(layerId, opacity) {
        if (!this.layerExists(layerId)) {
            return;
        }

        this.map.setPaintProperty(layerId, 'fill-opacity', opacity);
    }

    deleteLayerAndSource(imageId) {
        try {
            if (this.layerExists(imageId)) {
                this.map.removeLayer(imageId);
            }
            
            if (this.sourceExists(imageId)) {
                this.map.removeSource(imageId);
            }
        } catch(e) {}
    }

    sourceExists(sourceId) {
        return !!this.map.getSource(sourceId);
    }
      
    layerExists(layerId) {
        return !!this.map.getLayer(layerId);
    }

    addLine({ layerId, coordinates, color = '#ffffff', width = 2 }) {
        const data = {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'LineString',
                coordinates
            }
        };

        if (this.sourceExists(layerId)) {
            this.map.getSource(layerId).setData(data);

            if (this.layerExists(layerId)) {
                this.map.setLayoutProperty(layerId, 'visibility', 'visible');
                return;
            }
        }

        this.map.addSource(layerId, {
            type: 'geojson',
            data
        });

        this.map.addLayer({
            id: layerId,
            source: layerId,
            type: 'line',
            paint: {
                'line-color': color,
                'line-width': width,
                'line-opacity': 0.95
            }
        });
    }

    addAmbient({ landId, geojson, layerId, opacity = 1 }) {
        const sourceName = layerId || `${landId}-ambient`;
        this.deleteLayerAndSource(sourceName);

        this.map.addSource(sourceName, {
            'type': 'geojson',
            'data': geojson
        });

        this.map.addLayer({
            'id': sourceName,
            'source': sourceName,
            'type': 'fill',
            'paint': {
                'fill-color': ['get', 'color'],
                'fill-opacity': opacity
            }
        });
        return sourceName;
    }

    removeAmbient({ landId, layerId }) {
        const sourceName = layerId || `${landId}-ambient`;
        this.deleteLayerAndSource(sourceName)
        return sourceName;
    }

    addSoilMap({ landId, geojson, layerId, opacity = 0.82 }) {
        const sourceName = layerId || `soil-map-${landId}`;
        const outlineLayer = `${sourceName}-outline`;
        this.removeSoilMap({ landId, layerId: sourceName });

        this.map.addSource(sourceName, {
            type: 'geojson',
            data: geojson
        });

        const beforeLayer = this.layerExists('land_lines') ? 'land_lines' : undefined;
        const addLayer = (layer) => {
            if (beforeLayer) {
                this.map.addLayer(layer, beforeLayer);
                return;
            }

            this.map.addLayer(layer);
        };

        addLayer({
            id: sourceName,
            source: sourceName,
            type: 'fill',
            paint: {
                'fill-color': ['get', 'color'],
                'fill-opacity': opacity
            }
        });

        addLayer({
            id: outlineLayer,
            source: sourceName,
            type: 'line',
            paint: {
                'line-color': '#475569',
                'line-opacity': 0.9,
                'line-width': 1
            }
        });

        return sourceName;
    }

    removeSoilMap({ landId, layerId }) {
        const sourceName = layerId || `soil-map-${landId}`;
        const outlineLayer = `${sourceName}-outline`;

        try {
            if (this.layerExists(outlineLayer)) {
                this.map.removeLayer(outlineLayer);
            }
            if (this.layerExists(sourceName)) {
                this.map.removeLayer(sourceName);
            }
            if (this.sourceExists(sourceName)) {
                this.map.removeSource(sourceName);
            }
        } catch (error) {}

        return sourceName;
    }
}
